/**
 * Runtime source of truth for allowed analytics providers and their origins.
 *
 * This is hard-coded in the repository; the database catalogue can narrow or
 * equal it but can never widen it. This prevents an admin from loading an
 * arbitrary third-party script.
 */
const GTM = {
    provider: 'GTM',
    type: 'ANALYTICS',
    displayName: 'Google Tag Manager',
    consentCategory: 'ANALYTICS',
    scriptOrigins: ['https://www.googletagmanager.com'],
    connectOrigins: ['https://www.google-analytics.com', 'https://region1.google-analytics.com'],
    imageOrigins: ['https://www.google-analytics.com', 'https://*.google.com'],
    frameOrigins: ['https://www.googletagmanager.com'],
    allowedConfigurationKeys: [
        'containerId',
        'dataLayerName',
        'auth',
        'preview',
        'legacyCompatibility',
    ],
};
const GA4 = {
    provider: 'GA4',
    type: 'ANALYTICS',
    displayName: 'Google Analytics 4',
    consentCategory: 'ANALYTICS',
    scriptOrigins: ['https://www.googletagmanager.com', 'https://www.google-analytics.com'],
    connectOrigins: ['https://www.google-analytics.com', 'https://region1.google-analytics.com'],
    imageOrigins: ['https://www.google-analytics.com', 'https://*.google.com'],
    frameOrigins: [],
    allowedConfigurationKeys: ['measurementId'],
};
const TRIPTEASE = {
    provider: 'TRIPTEASE',
    type: 'ANALYTICS',
    displayName: 'Triptease',
    consentCategory: 'MARKETING',
    scriptOrigins: ['https://onboard.triptease.io'],
    connectOrigins: ['https://onboard.triptease.io'],
    imageOrigins: [],
    frameOrigins: [],
    allowedConfigurationKeys: ['integrationId', 'hotelId', 'environment'],
};
const PROVIDER_SCRIPT = {
    provider: 'PROVIDER_SCRIPT',
    type: 'ANALYTICS',
    displayName: 'Managed Provider Script',
    consentCategory: 'ANALYTICS',
    // Concrete managed providers only — this list is the upper bound and the
    // integration_providers catalogue can narrow it but never widen it.
    // Currently registered: Microsoft Clarity (consent category ANALYTICS).
    scriptOrigins: ['https://www.clarity.ms'],
    scriptPathPrefixes: ['/tag/'],
    connectOrigins: [],
    imageOrigins: [],
    frameOrigins: [],
    allowedConfigurationKeys: ['src', 'async', 'defer', 'crossOrigin', 'type', 'integrity'],
};
const PIXEL = {
    provider: 'PIXEL',
    type: 'ANALYTICS',
    displayName: 'Image Pixel / Beacon',
    consentCategory: 'MARKETING',
    scriptOrigins: [],
    connectOrigins: [],
    imageOrigins: ['https://www.google-analytics.com', 'https://region1.google-analytics.com'],
    frameOrigins: [],
    allowedConfigurationKeys: ['src'],
};
export const PROVIDER_ALLOWLIST = {
    GTM,
    GA4,
    TRIPTEASE,
    PROVIDER_SCRIPT,
    PIXEL,
};
export function getProviderDefinition(provider) {
    return PROVIDER_ALLOWLIST[provider];
}
export function isAllowedProvider(provider) {
    return Object.hasOwn(PROVIDER_ALLOWLIST, provider);
}
/**
 * Validate a configured script `src` against the provider's allowlisted
 * base domains. Returns the origin if valid, otherwise `null`.
 */
export function validateScriptOrigin(definition, src) {
    const origin = validateOrigin(src, definition.scriptOrigins);
    if (origin === null)
        return null;
    if (definition.scriptPathPrefixes !== undefined) {
        try {
            const { pathname } = new URL(src);
            if (!definition.scriptPathPrefixes.some((prefix) => pathname.startsWith(prefix))) {
                return null;
            }
        }
        catch {
            return null;
        }
    }
    return origin;
}
/**
 * Validate a configured image pixel `src` against the provider's allowlisted
 * image origins. Returns the origin if valid, otherwise `null`.
 */
export function validateImageOrigin(definition, src) {
    return validateOrigin(src, definition.imageOrigins);
}
function validateOrigin(src, allowed) {
    try {
        const url = new URL(src);
        if (url.protocol !== 'https:')
            return null;
        const origin = `${url.protocol}//${url.host}`;
        return allowed.includes(origin) ? origin : null;
    }
    catch {
        return null;
    }
}
/**
 * Server-side validation of a provider configuration payload.
 *
 * Returns `null` when the configuration is valid. Returns a human-readable
 * reason when it is not. This must be the authoritative validation gate; the
 * frontend may pre-validate, but it cannot be trusted.
 */
export function validateProviderConfiguration(provider, configuration) {
    if (!isAllowedProvider(provider)) {
        return `unknown provider: ${provider}`;
    }
    const definition = getProviderDefinition(provider);
    if (definition === undefined) {
        return `unknown provider: ${provider}`;
    }
    if (configuration === null || typeof configuration !== 'object' || Array.isArray(configuration)) {
        return 'configuration must be an object';
    }
    const config = configuration;
    const allowed = new Set(definition.allowedConfigurationKeys);
    for (const key of Object.keys(config)) {
        if (!allowed.has(key)) {
            return `unknown configuration key: ${key}`;
        }
    }
    for (const [key, value] of Object.entries(config)) {
        const reason = validateConfigField(provider, key, value);
        if (reason !== null)
            return reason;
    }
    return null;
}
function validateConfigField(provider, key, value) {
    if (value === null || value === undefined)
        return null;
    if (key === 'src') {
        if (typeof value !== 'string' || value.length === 0) {
            return 'src must be a non-empty string';
        }
        const definition = getProviderDefinition(provider);
        if (definition === undefined)
            return `unknown provider: ${provider}`;
        if (provider === 'PIXEL') {
            if (validateImageOrigin(definition, value) === null) {
                return 'pixel src origin is not allowlisted or is not HTTPS';
            }
            return null;
        }
        if (validateScriptOrigin(definition, value) === null) {
            return 'script src origin is not allowlisted or is not HTTPS';
        }
        return null;
    }
    if (['containerId', 'measurementId', 'integrationId', 'hotelId'].includes(key)) {
        if (typeof value !== 'string' || value.length === 0) {
            return `${key} must be a non-empty string`;
        }
    }
    if (key === 'dataLayerName') {
        if (typeof value !== 'string' || value.length === 0) {
            return 'dataLayerName must be a non-empty string';
        }
    }
    if (['auth', 'preview', 'environment', 'crossOrigin', 'type', 'integrity'].includes(key)) {
        if (typeof value !== 'string') {
            return `${key} must be a string`;
        }
    }
    if (['async', 'defer', 'legacyCompatibility'].includes(key)) {
        if (typeof value !== 'boolean') {
            return `${key} must be a boolean`;
        }
    }
    return null;
}
//# sourceMappingURL=provider-allowlist.js.map